/**
 * OCHAT OVERLAY: swaps upstream's brand name for the configured installation name
 * across every translated string, at translation time.
 *
 * This replaces the rebranding step that used to run in docker/Dockerfile:
 * a `sed -i 's/Chatwoot/Ochat/g'` over the ALREADY COMPILED assets in
 * /app/public/{vite,packs}, guarded with `|| true`. That approach had two
 * problems worth spelling out, because they are the reason this file exists:
 *
 *   1. It failed silently. If upstream ever changed where Vite writes its
 *      output, the sed matched nothing, the build still succeeded, and the UI
 *      quietly went back to saying "Chatwoot". Across 555 upstream commits that
 *      is not a safe bet.
 *   2. It also deleted the precompressed .br/.gz files to stop them from serving
 *      stale text, which threw away asset compression.
 *
 * Doing it here means new strings from upstream are rebranded automatically, and
 * nothing has to be patched after the build.
 *
 * The replacement is deliberately case-sensitive: it rewrites the display name
 * "Chatwoot" but leaves lowercase hostnames like chatwoot.com and chwt.app
 * alone, so links to upstream documentation keep working. Same semantics as
 * shared/composables/useBranding.js#replaceInstallationName, applied to the whole
 * catalogue in one place instead of component by component.
 */
const UPSTREAM_BRAND = 'Chatwoot';

/**
 * Reads the installation name straight off window.globalConfig rather than the
 * Vuex getter: postTranslation runs during rendering, outside component setup,
 * and the layout defines window.globalConfig before the bundle boots.
 */
const installationName = () => window.globalConfig?.INSTALLATION_NAME;

/**
 * vue-i18n postTranslation hook. Receives every resolved translation.
 * @param {unknown} value resolved message (not always a string)
 * @returns {unknown} the message with the brand name swapped
 */
export const brandPostTranslation = value => {
  if (typeof value !== 'string' || !value.includes(UPSTREAM_BRAND))
    return value;

  const name = installationName();
  if (!name || name === UPSTREAM_BRAND) return value;

  return value.replaceAll(UPSTREAM_BRAND, name);
};

export default brandPostTranslation;
